import React, { useState } from 'react';

import classNames from 'classnames';
import { AlertTriangle, Plus } from 'lucide-react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { DotBricksLoading, ListPagination, Search } from 'src/common/components';

import { fetchRequests } from '../api';
import {
  REQUESTS_QUERY_KEY,
  REQUEST_TYPE_LABEL,
  REQUEST_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from '../constants';
import { ProvisioningRequestRow, QueueFilters, RequestStatus, RequestType } from '../types';
import { daysWaiting, formatDate, isOverdue } from '../utils';

import NewRequestModal from './NewRequestModal';
import StatusBadge from './StatusBadge';

const PAGE_SIZE = 20;

const CLOSED_TOGGLE_DISABLED_REASON =
  'Showing the selected status only — clear it to switch between open and closed.';

const INITIAL_FILTERS: QueueFilters = {
  search: '',
  requestType: '',
  status: '',
  showClosed: false,
};

const AlertTriangle14 = (): JSX.Element => <AlertTriangle size={14} />;
const Plus16 = (): JSX.Element => <Plus size={16} />;

const FilterChip: React.FC<{
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ label, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={classNames(
      'px-3 py-1 text-sm rounded-full border transition-colors',
      isActive
        ? 'bg-primary-2.0 text-white-1.0 border-primary-2.0'
        : 'bg-white-1.0 text-gray-600 border-gray-300 hover:border-primary-2.0'
    )}
  >
    {label}
  </button>
);

const RequestQueue: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<QueueFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading } = useQuery([REQUESTS_QUERY_KEY, filters, page], fetchRequests);

  const rows = data?.data.results ?? [];
  const count = data?.data.count ?? 0;

  const updateFilters = (partial: Partial<QueueFilters>): void => {
    setFilters((current) => ({ ...current, ...partial }));
    setPage(1);
  };

  const handleTypeChip = (requestType: RequestType | ''): void => {
    updateFilters({ requestType });
  };

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full px-4 py-8 overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2>Provisioning requests</h2>
          <p className="mt-1 text-sm text-gray-500">
            SIP trunks and digital queue equipment requested by Sales and Customer Success.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary flex items-center gap-2"
          onClick={() => setIsCreating(true)}
        >
          <Plus16 />
          New request
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Search
          setSearch={(search: string) => updateFilters({ search })}
          placeholder="Client or contact name"
          className="sm:w-64"
        />
        <div className="flex items-center gap-2">
          <FilterChip
            label="All types"
            isActive={filters.requestType === ''}
            onClick={() => handleTypeChip('')}
          />
          {REQUEST_TYPE_OPTIONS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              isActive={filters.requestType === option.value}
              onClick={() => handleTypeChip(option.value)}
            />
          ))}
        </div>
        <select
          className="input w-48"
          aria-label="Status"
          value={filters.status}
          onChange={(event) => updateFilters({ status: event.target.value as RequestStatus | '' })}
        >
          <option value="">Any status</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label
          className={classNames(
            'flex items-center gap-2 ml-auto text-sm',
            filters.status ? 'text-gray-400' : 'text-gray-600'
          )}
          // A disabled input is not focusable, so the reason has to live on the
          // label — and be readable, not just a tooltip.
          title={filters.status ? CLOSED_TOGGLE_DISABLED_REASON : undefined}
        >
          <input
            type="checkbox"
            checked={filters.showClosed}
            disabled={!!filters.status}
            onChange={(event) => updateFilters({ showClosed: event.target.checked })}
          />
          Show completed and rejected
        </label>
      </div>

      {!!filters.status && (
        <p className="mb-4 -mt-2 text-xs text-gray-500">{CLOSED_TOGGLE_DISABLED_REASON}</p>
      )}

      {isLoading && <DotBricksLoading />}

      {!isLoading && rows.length === 0 && (
        <p className="py-12 text-center text-gray-500">
          {filters.showClosed
            ? 'No requests match these filters.'
            : 'Nothing pending. New requests will show up here.'}
        </p>
      )}

      {!isLoading && rows.length > 0 && (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Client</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Requested by</th>
              <th className="py-2 pr-4 font-medium">Assigned to</th>
              <th className="py-2 pr-4 font-medium">Needed by</th>
              <th className="py-2 pr-4 font-medium">Waiting</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: ProvisioningRequestRow) => (
              <tr
                key={row.id}
                className="hover:bg-white-1.0 focus:bg-white-1.0 border-b cursor-pointer"
                tabIndex={0}
                role="link"
                aria-label={`Open request for ${row.client_name}`}
                onClick={() => navigate(row.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(row.id);
                  }
                }}
              >
                <td className="py-3 pr-4 font-medium">{row.client_name}</td>
                <td className="py-3 pr-4">{REQUEST_TYPE_LABEL[row.request_type]}</td>
                <td className="py-3 pr-4">
                  <StatusBadge status={row.status} />
                </td>
                <td className="py-3 pr-4">{row.requester_display || '—'}</td>
                <td className="py-3 pr-4">{row.assignee_display || 'Unassigned'}</td>
                <td className="py-3 pr-4">
                  <span className="flex items-center gap-1">
                    {formatDate(row.needed_by)}
                    {isOverdue(row.needed_by, row.status) && (
                      // Text, not just the red triangle: colour alone is not a
                      // state anyone can rely on.
                      <span className="flex items-center gap-1 text-xs font-medium text-red-600">
                        <AlertTriangle14 />
                        Past due
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-3 pr-4 text-gray-500">{daysWaiting(row.created_at)}d</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {count > PAGE_SIZE && (
        <div className="mt-4">
          <ListPagination totalCount={count} currentPage={page} setCurrentPage={setPage} />
        </div>
      )}

      {isCreating && <NewRequestModal onClose={() => setIsCreating(false)} />}
    </div>
  );
};

export default RequestQueue;
